import React, { useEffect, useMemo, useState } from 'react';
import BillingPicker from './BillingPicker';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  parseBillingTotals,
  normalizeDailyBillingEntry,
} from './billingMetricsUtils';
import {
  resolveBillingTimeZone,
  getCurrentMonthKey,
  buildMonthPickerOptions,
  getMonthDateRange,
  formatMonthRangeLabel,
} from './billingDateUtils';
import { formatLocationLabel } from './billingDirectoryUtils';
import { navigate } from '@reach/router';
import HorizontalScrollRow from './HorizontalScrollRow';

const styles = (theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
  },
  metricRowWrapper: {
    marginBottom: theme.spacing(3),
  },
  metricRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 16,
    paddingBottom: 12,
    alignItems: 'stretch',
  },
  locationCarousel: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 12,
    paddingBottom: 8,
    marginTop: 4,
  },
  metricCard: {
    flex: '0 0 calc((100% - 80px) / 6)',
    minWidth: 220,
    minHeight: 190,
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderBottom: '4px solid #cfd8e3',
    boxShadow: '0 10px 25px -18px rgba(15, 23, 42, 0.35)',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    position: 'relative',
    overflow: 'hidden',
    color: '#0f172a',
  },
  metricLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: 12,
  },
  metricValue: {
    fontSize: '1.85rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 6,
  },
  metricSubtext: {
    fontSize: '0.85rem',
    color: '#374151',
    lineHeight: 1.35,
    borderTop: '1px solid #d7dee5',
    paddingTop: 8,
    marginTop: 8,
  },
  metricCardTotal: {
    background: '#f5f7ff',
    borderBottomColor: '#8da2fb',
  },
  metricCardSms: {
    background: '#edf9fb',
    borderBottomColor: '#16a3b8',
  },
  metricCardMms: {
    background: '#f8f2ff',
    borderBottomColor: '#c084fc',
  },
  metricCardVoice: {
    background: '#eefbf3',
    borderBottomColor: '#22c55e',
  },
  metricCardVideo: {
    background: '#fff4ed',
    borderBottomColor: '#fb923c',
  },
  metricCardFees: {
    background: '#f8fafc',
    borderBottomColor: '#64748b',
  },
  metricCardStatus: {
    background: '#fff5d8',
    borderBottomColor: '#f4b740',
    color: '#5c2b00',
  },
  metricCardCarrier: {
    background: '#eef5ff',
    borderBottomColor: '#60a5fa',
    color: '#14213d',
  },
  metricCardFailure: {
    background: '#ffe9e9',
    borderBottomColor: '#fb7185',
    color: '#7f1d1d',
  },
  sectionDivider: {
    borderTop: '1px solid #cdd5e2',
    paddingTop: 8,
    marginTop: 8,
  },
  directionSection: {
    fontSize: '0.85rem',
    color: '#374151',
    lineHeight: 1.35,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '8px 0',
    borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
  },
  breakdownLabel: {
    fontWeight: 600,
    color: '#0f172a',
  },
  breakdownDetails: {
    fontSize: '0.8rem',
    color: 'rgba(15, 23, 42, 0.7)',
  },
  breakdownValue: {
    fontWeight: 600,
    color: '#0f172a',
    marginLeft: 12,
    whiteSpace: 'nowrap',
  },
  breakdownMeta: {
    fontSize: '0.82rem',
    color: 'rgba(15, 23, 42, 0.7)',
  },
  breakdownSubtitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#475569',
    marginTop: 12,
    marginBottom: 4,
  },
  card: {
    marginTop: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow:
      '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.08)',
    gap: theme.spacing(2),
  },
  filterControls: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
});

const TWILIO_COLORS = [
  '#1b73e8',
  '#34a853',
  '#fbbc05',
  '#ea4335',
  '#a142f4',
  '#00acc1',
  '#fb8c00',
  '#0d9488',
  '#ef4444',
  '#6366f1',
  '#f472b6',
  '#2dd4bf',
];

const BillingAnalytics = ({ classes }) => {
  const { t } = useTranslation();
  const timezone = useMemo(() => resolveBillingTimeZone(), []);
  const monthOptions = useMemo(
    () => buildMonthPickerOptions({ timezone }),
    [timezone]
  );
  const [selectedMonth, setSelectedMonth] = useState(() =>
    getCurrentMonthKey(timezone)
  );
  const monthRange = useMemo(
    () => getMonthDateRange(selectedMonth, timezone),
    [selectedMonth, timezone]
  );
  const [accountId, setAccountId] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [accountOptions, setAccountOptions] = useState([
    { value: 'all', label: 'All accounts' },
  ]);
  const [locationOptions, setLocationOptions] = useState([
    { value: 'all', label: 'All locations' },
  ]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch directory of accounts/locations for filters
  useEffect(() => {
    let cancelled = false;
    async function fetchDirectory() {
      try {
        const params = new URLSearchParams({
          startDate: monthRange.startDate,
          endDate: monthRange.endDate,
          timezone,
        });
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/usage-metrics?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization:
                'Bearer ' +
                JSON.parse(sessionStorage.getItem('user')).signInUserSession
                  .idToken.jwtToken,
            },
          }
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = await res.json();
        if (cancelled) return;
        const accounts = buildAccountOptions(body && body.uiAccounts);
        setAccountOptions(accounts);
        if (body && (body.uiLocations || body.locationTraffic)) {
          const locations = buildLocationOptions(body, accountId);
          setLocationOptions(locations);
          // Reset location if it no longer belongs to selected account
          if (!locations.some((opt) => opt.value === locationId)) {
            setLocationId('all');
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    fetchDirectory();
    return () => {
      cancelled = true;
    };
  }, [monthRange.startDate, monthRange.endDate, timezone, accountId, locationId]);

  // Fetch usage metrics for chart
  useEffect(() => {
    let cancelled = false;
    async function fetchMetrics() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          startDate: monthRange.startDate,
          endDate: monthRange.endDate,
          timezone,
        });
        if (accountId !== 'all') {
          params.append('accountIds', accountId);
        }
        if (locationId !== 'all') {
          params.append('boxIds', locationId);
        }
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/usage-metrics?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization:
                'Bearer ' +
                JSON.parse(sessionStorage.getItem('user')).signInUserSession
                  .idToken.jwtToken,
            },
          }
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = await res.json();
        if (cancelled) return;
        setSummary(body || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setSummary(null);
          setError('Unable to load messaging metrics.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [accountId, locationId, monthRange.startDate, monthRange.endDate, timezone]);

  const chartPayload = useMemo(
    () => buildSpendChart(summary, locationId),
    [summary, locationId]
  );
  const currency =
    (summary && summary.currency) ? summary.currency : 'USD';

  const billingTotals = parseBillingTotals((summary && summary.totals) || {});
  const {
    sms: smsTotals,
    mms: mmsTotals,
    voice: voiceTotals,
    video: videoTotals,
    carrierCharges,
    a2pRegistrationCharges,
    totalSpend: billingTotalSpend,
  } = billingTotals;
  const statusBreakdown = aggregateBreakdown(summary, 'statuses');
  const failureSummary = buildFailureSummary(summary);

  const locationColorMap = useMemo(() => {
    const map = {};
    const locations = ensureArray(
      summary &&
        summary.locationTraffic &&
        summary.locationTraffic.locations
    );
    locations.forEach((location, index) => {
      const id =
        (location &&
          (location.boxId || location.id || location.boxName)) ||
        `loc-${index}`;
      map[String(id)] =
        TWILIO_COLORS[index % TWILIO_COLORS.length];
    });
    return map;
  }, [summary]);

  const locationCards = useMemo(
    () => buildLocationCards(summary, locationId, locationColorMap),
    [summary, locationId, locationColorMap]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      colors: TWILIO_COLORS,
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#edeff5',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: chartPayload.categories,
        labels: {
          style: { color: '#6b7280', fontSize: '12px' },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }) =>
          buildTooltip(
            chartPayload.series,
            chartPayload.categories,
            currency,
            dataPointIndex
          ),
      },
      yaxis: {
        labels: {
          formatter: (value) => formatCurrency(value, currency),
        },
      },
    }),
    [chartPayload, currency]
  );

  return (
    <div className={classes.container}>
      <Typography variant="h5" gutterBottom>
        {t('Billing.Analytics.Title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t('Billing.Analytics.Description')}
      </Typography>

      <div className={classes.filterRow}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/home/billing')}
        >
          Back to Billing
        </Button>
        <div className={classes.filterControls}>
          <BillingPicker
            label="Month"
            value={selectedMonth}
            options={monthOptions}
            onChange={setSelectedMonth}
          />
          <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>
            {formatMonthRangeLabel(selectedMonth, timezone)}
          </Typography>
          <BillingPicker
            label="Account"
            value={accountId}
            options={accountOptions}
            onChange={setAccountId}
          />
          <BillingPicker
            label="Location"
            value={locationId}
            options={locationOptions}
            onChange={setLocationId}
          />
        </div>
      </div>

      <HorizontalScrollRow
        className={classes.metricRowWrapper}
        contentClassName={classes.metricRow}
      >
        <div className={`${classes.metricCard} ${classes.metricCardTotal}`}>
          <div className={classes.metricLabel}>TOTAL SPEND</div>
          <div className={classes.metricValue}>
            {formatCurrency(billingTotalSpend, currency)}
          </div>
          <div className={classes.metricSubtext}>
            <div>SMS: {formatCurrency(smsTotals.spend, currency)}</div>
            <div>MMS: {formatCurrency(mmsTotals.spend, currency)}</div>
            <div>Voice: {formatCurrency(voiceTotals.spend, currency)}</div>
            <div>Video: {formatCurrency(videoTotals.spend, currency)}</div>
            {(carrierCharges > 0 || a2pRegistrationCharges > 0) && (
              <div>
                Fees:{' '}
                {formatCurrency(carrierCharges + a2pRegistrationCharges, currency)}
              </div>
            )}
          </div>
          <div className={classes.metricSubtext}>
            {summary && summary.range
              ? `${moment(summary.range.startDate).format(
                  'MMM D, YYYY'
                )} – ${moment(summary.range.endDate).format('MMM D, YYYY')}${
                  summary.range.timezone ? ` • ${summary.range.timezone}` : ''
                }`
              : '\u00A0'}
          </div>
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardSms}`}>
          <div className={classes.metricLabel}>SMS</div>
          <div className={classes.metricValue}>
            {formatCurrency(smsTotals.spend, currency)}
          </div>
          <div className={classes.metricSubtext}>
            <div>Messages: {formatNumber(smsTotals.count)}</div>
            <div>Segments: {formatNumber(smsTotals.segments)}</div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Outbound</div>
              {smsTotals.outbound.count || smsTotals.outbound.segments || smsTotals.outbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Messages: {formatNumber(smsTotals.outbound.count)}</div>
                  <div>Segments: {formatNumber(smsTotals.outbound.segments)}</div>
                  <div>Spend: {formatCurrency(smsTotals.outbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Inbound</div>
              {smsTotals.inbound.count || smsTotals.inbound.segments || smsTotals.inbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Messages: {formatNumber(smsTotals.inbound.count)}</div>
                  <div>Segments: {formatNumber(smsTotals.inbound.segments)}</div>
                  <div>Spend: {formatCurrency(smsTotals.inbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
          </div>
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardMms}`}>
          <div className={classes.metricLabel}>MMS</div>
          <div className={classes.metricValue}>
            {formatCurrency(mmsTotals.spend, currency)}
          </div>
          <div className={classes.metricSubtext}>
            <div>Messages: {formatNumber(mmsTotals.count)}</div>
            <div>Segments: {formatNumber(mmsTotals.segments)}</div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Outbound</div>
              {mmsTotals.outbound.count || mmsTotals.outbound.segments || mmsTotals.outbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Messages: {formatNumber(mmsTotals.outbound.count)}</div>
                  <div>Segments: {formatNumber(mmsTotals.outbound.segments)}</div>
                  <div>Spend: {formatCurrency(mmsTotals.outbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Inbound</div>
              {mmsTotals.inbound.count || mmsTotals.inbound.segments || mmsTotals.inbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Messages: {formatNumber(mmsTotals.inbound.count)}</div>
                  <div>Segments: {formatNumber(mmsTotals.inbound.segments)}</div>
                  <div>Spend: {formatCurrency(mmsTotals.inbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
          </div>
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardVoice}`}>
          <div className={classes.metricLabel}>VOICE</div>
          <div className={classes.metricValue}>
            {formatCurrency(voiceTotals.spend, currency)}
          </div>
          <div className={classes.metricSubtext}>
            <div>Calls: {formatNumber(voiceTotals.count)}</div>
            <div>Minutes: {formatNumber(voiceTotals.minutes)}</div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Outbound</div>
              {voiceTotals.outbound.count || voiceTotals.outbound.minutes || voiceTotals.outbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Calls: {formatNumber(voiceTotals.outbound.count)}</div>
                  <div>Minutes: {formatNumber(voiceTotals.outbound.minutes)}</div>
                  <div>Spend: {formatCurrency(voiceTotals.outbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Inbound</div>
              {voiceTotals.inbound.count || voiceTotals.inbound.minutes || voiceTotals.inbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Calls: {formatNumber(voiceTotals.inbound.count)}</div>
                  <div>Minutes: {formatNumber(voiceTotals.inbound.minutes)}</div>
                  <div>Spend: {formatCurrency(voiceTotals.inbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
          </div>
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardVideo}`}>
          <div className={classes.metricLabel}>VIDEO</div>
          <div className={classes.metricValue}>
            {formatCurrency(videoTotals.spend, currency)}
          </div>
          <div className={classes.metricSubtext}>
            <div>Rooms: {formatNumber(videoTotals.rooms)}</div>
            <div>Participant minutes: {formatNumber(videoTotals.participantMinutes)}</div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Outbound</div>
              {videoTotals.outbound.rooms || videoTotals.outbound.participantMinutes || videoTotals.outbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Rooms: {formatNumber(videoTotals.outbound.rooms)}</div>
                  <div>Minutes: {formatNumber(videoTotals.outbound.participantMinutes)}</div>
                  <div>Spend: {formatCurrency(videoTotals.outbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
            <div className={classes.sectionDivider}>
              <div style={{ fontWeight: 600 }}>Inbound</div>
              {videoTotals.inbound.rooms || videoTotals.inbound.participantMinutes || videoTotals.inbound.spend ? (
                <div className={classes.directionSection}>
                  <div>Rooms: {formatNumber(videoTotals.inbound.rooms)}</div>
                  <div>Minutes: {formatNumber(videoTotals.inbound.participantMinutes)}</div>
                  <div>Spend: {formatCurrency(videoTotals.inbound.spend, currency)}</div>
                </div>
              ) : (
                <div className={classes.directionSection}>No traffic</div>
              )}
            </div>
          </div>
        </div>
        {(carrierCharges > 0 || a2pRegistrationCharges > 0) && (
          <div className={`${classes.metricCard} ${classes.metricCardFees}`}>
            <div className={classes.metricLabel}>CARRIER &amp; A2P</div>
            <div className={classes.metricValue}>
              {formatCurrency(carrierCharges + a2pRegistrationCharges, currency)}
            </div>
            <div className={classes.metricSubtext}>
              <div>Carrier charges: {formatCurrency(carrierCharges, currency)}</div>
              <div>A2P registration: {formatCurrency(a2pRegistrationCharges, currency)}</div>
            </div>
          </div>
        )}
        <div className={`${classes.metricCard} ${classes.metricCardStatus}`}>
          <div className={classes.metricLabel}>STATUS BREAKDOWN</div>
          {(statusBreakdown || []).length ? (
            (statusBreakdown || []).slice(0, 5).map((row, index, array) => (
              <div
                key={row.label}
                className={classes.breakdownRow}
                style={
                  index === array.length - 1 ? { borderBottom: 'none' } : {}
                }
              >
                <div>
                  <div className={classes.breakdownLabel}>
                    {formatTitleCase(row.label)}
                  </div>
                  <div className={classes.breakdownDetails}>
                    <div>Messages: {formatNumber(row.messages)}</div>
                    <div>Segments: {formatNumber(row.segments)}</div>
                  </div>
                </div>
                <div className={classes.breakdownValue}>
                  {formatCurrency(row.spend, currency)}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                color: 'rgba(15, 23, 42, 0.7)',
                textAlign: 'center',
                fontSize: '0.85rem',
                padding: '12px 0',
              }}
            >
              No status data available.
            </div>
          )}
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardCarrier}`}>
          <div className={classes.metricLabel}>SERVICE BREAKDOWN</div>
          {[
            { label: 'SMS', spend: smsTotals.spend, detail: `${formatNumber(smsTotals.count)} msgs` },
            { label: 'MMS', spend: mmsTotals.spend, detail: `${formatNumber(mmsTotals.count)} msgs` },
            { label: 'Voice', spend: voiceTotals.spend, detail: `${formatNumber(voiceTotals.minutes)} min` },
            { label: 'Video', spend: videoTotals.spend, detail: `${formatNumber(videoTotals.participantMinutes)} min` },
          ].map((row, index, array) => (
            <div
              key={row.label}
              className={classes.breakdownRow}
              style={index === array.length - 1 ? { borderBottom: 'none' } : {}}
            >
              <div>
                <div className={classes.breakdownLabel}>{row.label}</div>
                <div className={classes.breakdownDetails}>
                  <div>{row.detail}</div>
                </div>
              </div>
              <div className={classes.breakdownValue}>
                {formatCurrency(row.spend, currency)}
              </div>
            </div>
          ))}
        </div>
        <div className={`${classes.metricCard} ${classes.metricCardFailure}`}>
          <div className={classes.metricLabel}>FAILURE SUMMARY</div>
          {failureSummary ? (
            <React.Fragment>
              <div className={classes.breakdownMeta}>
                <div>Total Failures: {formatNumber(failureSummary.totalMessages)} Messages</div>
                <div>Segments: {formatNumber(failureSummary.totalSegments)}</div>
                <div>Spend: {formatCurrency(failureSummary.totalSpend, currency)}</div>
              </div>
              {(failureSummary.sections || []).map((section, sectionIndex) => (
                <div key={section.title}>
                  <div
                    className={classes.breakdownSubtitle}
                    style={
                      sectionIndex > 0
                        ? {
                            borderTop: '1px solid rgba(15, 23, 42, 0.12)',
                            paddingTop: 12,
                            marginTop: 12,
                          }
                        : {}
                    }
                  >
                    {section.title}
                  </div>
                  {section.entries && section.entries.length ? (
                    section.entries.slice(0, 3).map((entry) => (
                      <div
                        key={`${section.title}-${entry.label}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '8px 0',
                        }}
                      >
                        <div>
                          <div className={classes.breakdownLabel}>
                            {formatTitleCase(entry.label || 'Unknown')}
                          </div>
                          <div className={classes.breakdownDetails}>
                            <div>
                              Messages:{' '}
                              {formatNumber(entry.messages || entry.totalMessages || 0)}
                            </div>
                            <div>
                              Segments:{' '}
                              {formatNumber(entry.segments || entry.totalSegments || 0)}
                            </div>
                          </div>
                        </div>
                        <div className={classes.breakdownValue}>
                          {formatCurrency(entry.spend || entry.totalSpend || 0, currency)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={classes.breakdownMeta}>No data</div>
                  )}
                </div>
              ))}
            </React.Fragment>
          ) : (
            <div
              style={{
                color: 'rgba(15, 23, 42, 0.7)',
                textAlign: 'center',
                fontSize: '0.85rem',
                padding: '12px 0',
              }}
            >
              No failures recorded for this range.
            </div>
          )}
        </div>
      </HorizontalScrollRow>

      <Card className={classes.card}>
        <CardContent>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : loading ? (
            <Typography variant="body2" color="textSecondary">
              Loading usage metrics…
            </Typography>
          ) : chartPayload.series.length && chartPayload.categories.length ? (
            <ReactApexChart
              options={chartOptions}
              series={chartPayload.series}
              type="line"
              height={360}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No usage metrics for this selection.
            </Typography>
          )}
        </CardContent>
      </Card>

      <div style={{ marginTop: 16 }}>
        {locationCards.length ? (
          <HorizontalScrollRow contentClassName={classes.locationCarousel}>
            {locationCards.map((card) => (
              <Card
                key={card.id}
                style={{
                  minWidth: 240,
                  flexShrink: 0,
                  borderRadius: 12,
                  border: '1px solid rgba(209,213,219,0.9)',
                }}
              >
                  <CardContent style={{ padding: '10px 12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: card.color,
                          marginRight: 6,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {card.name}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, marginBottom: 4 }}>
                      <div style={{ fontWeight: 600 }}>SMS</div>
                      <div>Messages: {card.sms.count}</div>
                      <div>Segments: {card.sms.segments}</div>
                      <div>
                        Spend:{' '}
                        {formatCurrency(card.sms.spend, currency)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, marginBottom: 4 }}>
                      <div style={{ fontWeight: 600 }}>MMS</div>
                      <div>Messages: {card.mms.count}</div>
                      <div>Segments: {card.mms.segments}</div>
                      <div>
                        Spend:{' '}
                        {formatCurrency(card.mms.spend, currency)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, marginBottom: 4 }}>
                      <div style={{ fontWeight: 600 }}>Voice</div>
                      <div>Calls: {card.voice.count}</div>
                      <div>Minutes: {card.voice.minutes}</div>
                      <div>
                        Spend:{' '}
                        {formatCurrency(card.voice.spend, currency)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11 }}>
                      <div style={{ fontWeight: 600 }}>Video</div>
                      <div>Rooms: {card.video.rooms}</div>
                      <div>Minutes: {card.video.participantMinutes}</div>
                      <div>
                        Spend:{' '}
                        {formatCurrency(card.video.spend, currency)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
            ))}
          </HorizontalScrollRow>
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            No location details available.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(BillingAnalytics);

// Helpers copied/adapted from Twilio metrics dashboard

function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function buildAccountOptions(uiAccounts) {
  const base = ensureArray(uiAccounts).map((account) => {
    const raw =
      (account && (account.accountId || account.id || account.name)) ||
      'account';
    const label =
      (account && account.name) ||
      (account && account.displayName) ||
      (account && account.accountName) ||
      raw;
    return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All accounts' }, ...base];
}

function buildLocationOptions(payload, accountId) {
  let locations = ensureArray(payload && payload.uiLocations);
  if (!locations.length) {
    locations = ensureArray(
      payload && payload.locationTraffic && payload.locationTraffic.locations
    );
  }
  if (accountId && accountId !== 'all') {
    locations = locations.filter(
      (loc) => String(loc.accountId) === String(accountId)
    );
  }
  const base = locations.map((location) => {
    const raw =
      (location && (location.boxId || location.id || location.boxName)) ||
      'location';
    return {
      value: String(raw),
      label: formatLocationLabel(location),
    };
  });
  return [{ value: 'all', label: 'All locations' }, ...base];
}

function buildLocationCards(summary, locationId, colorMap) {
  const locations = ensureArray(
    summary &&
      summary.locationTraffic &&
      summary.locationTraffic.locations
  );
  return locations
    .filter((location) => {
      if (locationId === 'all') {
        return true;
      }
      return (
        String(location.boxId || location.id) === String(locationId)
      );
    })
    .map((location, index) => {
      const id = String(location.boxId || location.id || index);
      return {
        id,
        name: location.boxName || location.boxId || 'Location',
        ...parseBillingTotals(location && location.totals),
        color:
          (colorMap && colorMap[id]) ||
          TWILIO_COLORS[index % TWILIO_COLORS.length],
      };
    });
}

function buildSpendChart(summary, locationId) {
  const locations = ensureArray(
    summary && summary.locationTraffic && summary.locationTraffic.locations
  );
  if (!locations.length) {
    return { categories: [], series: [] };
  }
  const filtered = locations.filter((location) => {
    if (locationId === 'all') return true;
    return String(location.boxId || location.id) === String(locationId);
  });
  const dateSet = new Set();
  filtered.forEach((location) => {
    ensureArray(location.daily).forEach((entry) => {
      if (entry && entry.date) {
        dateSet.add(entry.date);
      }
    });
  });
  const dates = Array.from(dateSet).sort();
  if (!dates.length) {
    return { categories: [], series: [] };
  }
  const series = filtered
    .map((location) => {
      const lookup = new Map();
      ensureArray(location.daily).forEach((entry) => {
        if (!entry || !entry.date) return;
        lookup.set(entry.date, normalizeDailyEntry(entry));
      });
      const data = dates.map((date) => {
        const normalized = lookup.get(date) || createEmptyDaily();
        return {
          x: moment(date).format('MMM D'),
          y: Number(normalized.totalSpend) || 0,
          meta: {
            locationName: location.boxName || location.boxId || 'Location',
            totals: normalized,
          },
        };
      });
      const hasSpend = data.some((point) => Number(point.y) > 0);
      if (!hasSpend) {
        return null;
      }
      return {
        name: location.boxName || location.boxId || 'Location',
        data,
      };
    })
    .filter(Boolean);
  return {
    categories: dates.map((date) => moment(date).format('MMM D')),
    series,
  };
}

function normalizeDailyEntry(entry) {
  return normalizeDailyBillingEntry(entry || {});
}

function createEmptyDaily() {
  return normalizeDailyEntry({});
}

function formatCurrency(value, currency = 'USD') {
  const num = Number(value || 0);
  // Show exact decimal places from API response (up to 10 decimal places)
  // Use useGrouping: false to avoid comma separators that might interfere with precision
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 10,
    useGrouping: true,
  }).format(num);
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value) || 0);
}

function formatTitleCase(label) {
  if (!label) {
    return 'Unknown';
  }
  const upper = label.toUpperCase();
  if (upper === 'SMS' || upper === 'MMS') {
    return upper;
  }
  return label
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function toNumber(value) {
  return Number(value || 0);
}

function aggregateBreakdown(summary, key) {
  const locations = ensureArray(
    summary && summary.locationTraffic && summary.locationTraffic.locations
  );
  const map = new Map();
  locations.forEach((location) => {
    ensureArray(location && location[key]).forEach((entry) => {
      const label = (entry && entry.label) || 'Unknown';
      if (!map.has(label)) {
        map.set(label, { label, messages: 0, segments: 0, spend: 0 });
      }
      const bucket = map.get(label);
      bucket.messages += toNumber(
        (entry && (entry.messages || entry.totalMessages || entry.smsCount || entry.mmsCount))
      );
      bucket.segments += toNumber(
        (entry && (entry.segments || entry.totalSegments)) 
      );
      bucket.spend += toNumber(
        (entry && (entry.spend || entry.totalSpend))
      );
    });
  });
  return Array.from(map.values()).sort((a, b) => b.messages - a.messages);
}

function buildFailureSummary(summary) {
  const failure =
    summary &&
    summary.locationTraffic &&
    summary.locationTraffic.failureSummary;
  if (!failure) {
    return null;
  }
  return {
    totalMessages: failure.totalMessages || 0,
    totalSegments: failure.totalSegments || 0,
    totalSpend: failure.totalSpend || 0,
    sections: [
      { title: 'Statuses', entries: ensureArray(failure.statuses) },
      { title: 'Carriers', entries: ensureArray(failure.carriers) },
      { title: 'Error codes', entries: ensureArray(failure.errorCodes) },
    ],
  };
}

function buildTooltip(series, categories, currency, index) {
  const label = categories[index] || '';
  let rows = '';
  (series || []).forEach((entry, seriesIndex) => {
    const point = entry.data && entry.data[index];
    if (!point || Number(point.y) === 0) {
      return;
    }
    const color = TWILIO_COLORS[seriesIndex % TWILIO_COLORS.length];
    const totals = normalizeDailyBillingEntry((point.meta && point.meta.totals) || {});
    const sms = totals.sms || {};
    const mms = totals.mms || {};
    const voice = totals.voice || {};
    const video = totals.video || {};
    rows += `<div style="margin-bottom:8px;">
      <div style="display:flex;align-items:center;font-weight:600;margin-bottom:2px;">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;"></span>
        <span>${(point.meta && point.meta.locationName) || entry.name || 'Location'}</span>
        <span style="margin-left:auto;">${formatCurrency(
          totals.totalSpend || point.y || 0,
          currency
        )}</span>
      </div>
      <div style="font-size:11px;color:#6b7280;line-height:1.4;padding-left:16px;">
        <div>SMS: ${formatCurrency(sms.spend || 0, currency)} • ${sms.count || 0} msgs</div>
        <div>MMS: ${formatCurrency(mms.spend || 0, currency)} • ${mms.count || 0} msgs</div>
        <div>Voice: ${formatCurrency(voice.spend || 0, currency)} • ${voice.minutes || 0} min</div>
        <div>Video: ${formatCurrency(video.spend || 0, currency)} • ${video.participantMinutes || 0} min</div>
      </div>
    </div>`;
  });
  if (!rows) {
    rows =
      '<div class="text-muted" style="font-size:11px;">No spend recorded</div>';
  }
  return `<div class="px-2 py-1">
    <div style="font-weight:600;margin-bottom:6px;">${label}</div>
    ${rows}
  </div>`;
}




// WEBPACK FOOTER //
// ./src/components/billing/BillingAnalytics.js